import { useCallback, useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { RiCloseLine } from '@/shared/icons/remix'
import {
  consentAcceptButtonSx,
  consentBodyTextSx,
  consentDeclineButtonSx,
  consentFooterSx,
  consentLastUpdatedSx,
  consentPolicyTitleSx,
  consentScrollRegionSx,
} from '../styles/instrumentOnboardingStyles'
import {
  webSurveyLegalAcceptDisabledSx,
  webSurveyLegalOverlayPaperSx,
} from '../styles/webSurveyAccountStyles'
import { signupSharePrimaryButtonSx } from '../styles/signupShareTheme'

interface WebSurveyLegalModalProps {
  readonly open: boolean
  readonly title: string
  readonly paragraphs: readonly string[]
  readonly lastUpdated: string
  readonly onClose: () => void
  /** Figma 144:6899 / 144:7000 — overlay with scroll-gated I Accept. */
  readonly acceptMode?: boolean
  readonly onAccept?: () => void
  readonly onDecline?: () => void
}

export function WebSurveyLegalModal({
  open,
  title,
  paragraphs,
  lastUpdated,
  onClose,
  acceptMode = false,
  onAccept,
  onDecline,
}: WebSurveyLegalModalProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [hasReachedBottom, setHasReachedBottom] = useState(false)

  const evaluateScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= 8) {
      setHasReachedBottom(true)
    }
  }, [])

  useEffect(() => {
    if (!open || !acceptMode) return
    const el = scrollRef.current
    if (!el) return
    el.scrollTop = 0
    setHasReachedBottom(false)
    const frame = requestAnimationFrame(() => {
      const node = scrollRef.current
      if (!node) return
      if (node.scrollHeight <= node.clientHeight + 8) {
        setHasReachedBottom(true)
      }
    })
    return () => cancelAnimationFrame(frame)
  }, [acceptMode, open, title])

  if (!acceptMode) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        data-testid="web-survey-legal-modal"
      >
        <DialogTitle sx={{ pr: 6 }}>
          {title}
          <IconButton
            aria-label="Close"
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            data-testid="web-survey-legal-modal-close"
          >
            <RiCloseLine size={20} aria-hidden />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {paragraphs.map((paragraph) => (
            <Typography
              key={paragraph.slice(0, 24)}
              component="p"
              sx={{ mb: 2, fontSize: 14, lineHeight: '22px' }}
            >
              {paragraph}
            </Typography>
          ))}
          <Typography component="p" sx={{ color: '#64748b', fontSize: 13 }}>
            {lastUpdated}
          </Typography>
        </DialogContent>
      </Dialog>
    )
  }

  const handleDismiss = () => {
    onDecline?.()
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleDismiss}
      data-testid="web-survey-legal-modal"
      BackdropProps={{ sx: { bgcolor: 'rgba(0, 0, 0, 0.5)' } }}
      PaperProps={{ sx: webSurveyLegalOverlayPaperSx }}
    >
      <Box
        ref={scrollRef}
        onScroll={evaluateScroll}
        data-testid="web-survey-legal-modal-scroll"
        sx={consentScrollRegionSx}
      >
        <Typography component="h2" sx={consentPolicyTitleSx}>
          {title}
        </Typography>
        {paragraphs.map((paragraph, index) => (
          <Typography key={`${index}-${paragraph.slice(0, 40)}`} component="p" sx={consentBodyTextSx}>
            {paragraph}
          </Typography>
        ))}
        <Typography component="p" sx={consentLastUpdatedSx}>
          {lastUpdated}
        </Typography>
      </Box>
      <Box sx={consentFooterSx}>
        <Button
          type="button"
          variant="contained"
          fullWidth
          disableElevation
          disabled={!hasReachedBottom}
          onClick={() => {
            onAccept?.()
          }}
          data-testid="web-survey-legal-modal-accept"
          sx={[
            consentAcceptButtonSx,
            signupSharePrimaryButtonSx,
            !hasReachedBottom ? webSurveyLegalAcceptDisabledSx : null,
          ]}
        >
          I Accept
        </Button>
        <Button
          type="button"
          variant="text"
          onClick={handleDismiss}
          data-testid="web-survey-legal-modal-decline"
          sx={consentDeclineButtonSx}
        >
          Decline
        </Button>
      </Box>
    </Dialog>
  )
}
